/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {useDebouncedEffect} from "./EffectHooks";
import {GuidUtils, ObjectUtils} from "../Utils";
import {useGuid, useValidations} from "./GenericModelHooks";
import {useCallback, useEffect, useRef} from "react";
import {useIsMounted} from "./useIsMounted";

export const useGenericModelValidations = (props, visibleFieldDefinitionGroups) => {

    const {
        Association: association,
        CustomData: customData,
        Data: data = {},
        DefaultSelectedFieldDefKey,
        EditMode: editMode,
        HideGroups: hiddenGroupKeys = [],
        HideFields: hiddenFieldKeys = [],
        IsNewModel,
        Model: model,
        OnValidationUpdate: onValidationUpdate,
        RootData: rootData,
        SkipBorder,
        Update: onUpdate
    } = props;

    const [validations, setValidations, isValid] = useValidations();
    const [childValidations, setChildValidations, isChildValid] = useValidations();
    const editorGuid = useGuid();

    // Track the serialized form of the last validation result so we can skip
    // setValidations when nothing actually changed.  Calling setValidations with
    // a new-but-equivalent object on every debounce cycle causes every GMV in
    // the tree to re-render at once (~4700 components) even when the user hasn't
    // changed any validated field.
    const prevValidationsStrRef = useRef('');

    const revalidateFields = async () => {
        let ret = {};

        for (const fieldGroup of visibleFieldDefinitionGroups) {

            for (const field of fieldGroup.value) {

                const { associationName, getter, key, model: associationModel, validators } = field;

                // Skip validation of foreign key fields in
                // child associations, because the server will
                // automatically fill in the value based on the
                // parent's key in the data structure
                if (association?.using === key) continue;

                ret[key] = {
                    id: key,
                    valid: true,
                    className: "",
                    message: ""
                };

                if (validators && validators.length) {
                    validators.forEach((validator) => {
                        const value = getter(data);
                        let modelToValidate = model;
                        let dataToValidate = data;

                        // send appropriate model and data to child model validators
                        if (associationName && associationModel) {
                            modelToValidate = associationModel;
                            dataToValidate = dataToValidate[associationName]
                        }

                        if (validator?.show(editMode)) {
                            if (!validator?.logic(value, modelToValidate, dataToValidate, customData)) {
                                ret[key] = {
                                    id: key,
                                    valid: false,
                                    className: validator?.className?.(modelToValidate) ?? 'sb-has-validation-message',
                                    message: validator?.message(modelToValidate)
                                }
                            }
                        }
                    });
                }
            }
        }

        if (model.recordValidators) {
            for (const validator of model.recordValidators) {
                if (validator.show(editMode)) {
                    const valid = await validator.logic(model, data);
                    if (!valid) {
                        ret[null] = {
                            id: GuidUtils.Generate(),
                            valid: false,
                            message: validator.message(model, data)
                        };
                    }
                }
            }
        }

        // Only update state when the validation results actually changed.
        // Validation objects are small, so JSON.stringify is cheap here.
        const nextStr = JSON.stringify(ret);
        if (nextStr !== prevValidationsStrRef.current) {
            prevValidationsStrRef.current = nextStr;
            setValidations(ret);
        }
    };

    const isMounted = useIsMounted();

    // perform validation on load
    useEffect(() => {
        if(isMounted()) {
            void revalidateFields();
        }
    }, []);

    // and also whenever any of our
    // visible values change
    useDebouncedEffect(async () => {
        if(isMounted()) {
            void revalidateFields();
        }
    }, [model, ObjectUtils.SafeStringify(visibleFieldDefinitionGroups), data], 300);

    const onChildValidation = useCallback((newChildValidation, editorGuid) => {
        // Use functional updater so this callback doesn't depend on childValidations
        // and stays stable — a changing callback would invalidate OnValidationUpdate
        // in additionalTypeProps and cause all list nodes to re-render on every
        // validation cycle.
        setChildValidations(prev => {
            // Remove the previous entry for this editorGuid, retain the rest
            const { [editorGuid]: _, ...next } = prev;

            // Note: we only propagate a single valid/invalid summary to the parent,
            // not the full field-level detail — each GMV shows its own messages.
            // An empty newChildValidation means the child has no validations at all.
            if (Object.keys(newChildValidation).length) {
                next[editorGuid] = {
                    valid: Object.values(newChildValidation).every(({ valid }) => valid)
                };
            }

            return next;
        });
    }, [setChildValidations])

    // Notify parent when our validation state changes so it can update the save button.
    useEffect(() => {
        onValidationUpdate?.(
            {...validations, ...childValidations},
            editorGuid
        );
    }, [JSON.stringify(validations), JSON.stringify(childValidations), editorGuid]);

    // Separate cleanup effect so we only clear the parent entry on unmount,
    // not on every validation update. The previous single-effect pattern fired
    // the cleanup (clearing the entry) before every re-notification, creating
    // a double-fire cascade: clear → set → parent re-renders → repeat.
    useEffect(() => {
        return () => { onValidationUpdate?.({}, editorGuid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { validations, childValidations, onChildValidation };
}
